import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { Grid, DialogContent } from "@mui/material"

import i18n from "config/i18n"
import { useAppDispatch } from "redux/hooks"
import {
  withModalStep,
  withModalProduct,
  withPaymentMethods,
} from "hocs/features"
import {
  closeDialog,
  updateDialogStep,
} from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import { showAlert } from "redux/features/notice/noticeSlice"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import NewPayment from "components/Products/Payment/Pay/New"
import NewTransfer from "components/Products/Payment/Pay/Transfer"
import { PAYMENT_STEP_INITIAL, PAYMENT_STEP_TRANSFER } from "config"
import { ProductPaymentMethodList, FetchStatusType, ProductType } from "types"

type Props = {
  product: ProductType
  paymentMethods: ProductPaymentMethodList
  step: number
  fetchStatus: FetchStatusType
}

const ProductPaymentModal = ({
  product,
  paymentMethods,
  step = PAYMENT_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { price, product_type } = product

  const isBackHidden = step !== PAYMENT_STEP_TRANSFER

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
  }

  const handlePaymentCreateFailure = (error: string) => {
    dispatch(
      showAlert({
        message: error,
      }),
    )
  }

  const handleBack = () => {
    switch (step) {
      case PAYMENT_STEP_TRANSFER:
        dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
        return
      default:
        return
    }
  }

  const STEPS = [
    {
      label: PAYMENT_STEP_INITIAL,
      title: t("features.payment.form.title"),
      component: (
        <NewPayment
          amount={price}
          product={product_type}
          paymentMethods={paymentMethods}
          onToggle={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_TRANSFER }))
          }
          onFailure={(error) => handlePaymentCreateFailure(error)}
        />
      ),
    },
    {
      label: PAYMENT_STEP_TRANSFER,
      title: t("features.payment.form.title"),
      component: (
        <NewTransfer
          product={product}
          onBanklink={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
          }
        />
      ),
    },
  ]

  return (
    <DialogContent>
      <Back hidden={isBackHidden} onClick={handleBack} />
      <Close onClick={handleClose} />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalProduct,
  withPaymentMethods,
)(ProductPaymentModal)
